import {
  adaptSegmentPreview,
  type CreateCreatorSegmentDto,
  handleSegmentFilter,
  handleSegmentPreviewParams,
  type PreviewCreatorSegmentDto,
  type Segment,
  type SegmentDetail,
  type SegmentPreviewFilter,
  SEGMENTS_PAGE_LIMIT,
  type SegmentsFilter,
  type UpdateCreatorSegmentDto,
  type UseGetSegmentPreviewReturn,
} from '@metapic-apps/shared'
import {
  computed,
  type ComputedRef,
  getCurrentScope,
  onScopeDispose,
  type Ref,
  ref,
  watch,
} from 'vue'

import type { AdvertiserType } from '@/lib'
import { handleErrorMessage } from '@/lib/helpers/error.helpers'
import { socialMediaClient } from '@/lib/services/social-media.service'
import { useAdvertiserStore } from '@/stores/advertiser.stores'
import { useErrorStore } from '@/stores/error.stores'

type UseGetSegmentsReturn = {
  data: Ref<Segment[]>
  totalPages: Ref<number>
  isLoading: Ref<boolean>
  error: Ref<Error | null>
  refetch: () => Promise<void>
}

export const useGetSegments = (
  filterRef: Ref<SegmentsFilter>,
  advertiser: ComputedRef<AdvertiserType | undefined>,
): UseGetSegmentsReturn => {
  const data = ref<Segment[]>([])
  const totalPages = ref<number>(0)
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const fetchSegments = async (filter: SegmentsFilter, advertiserId: number) => {
    try {
      data.value = []
      isLoading.value = true
      error.value = null

      const result = await socialMediaClient.creatorSegments.advertiserListSegments(
        advertiserId,
        handleSegmentFilter(filter),
      )
      data.value = result.items
      totalPages.value = result.meta.totalPages
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      isLoading.value = false
    }
  }

  const refetch = async () => {
    const advertiserId = advertiser.value?.id
    if (advertiserId) {
      await fetchSegments(filterRef.value, advertiserId)
    }
  }

  watch([filterRef, () => advertiser?.value], refetch, { immediate: true, deep: true })

  return {
    data,
    totalPages,
    isLoading,
    error,
    refetch,
  }
}

type UseGetSegmentReturn = {
  data: Ref<SegmentDetail | null>
  isLoading: Ref<boolean>
  error: Ref<Error | null>
  refetch: () => Promise<void>
}

export const useGetSegment = (id: Ref<string>): UseGetSegmentReturn => {
  const data = ref<SegmentDetail | null>(null)
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const fetchSegment = async (segmentId: string) => {
    try {
      isLoading.value = true
      error.value = null
      data.value = null
      data.value = await socialMediaClient.creatorSegments.findOne(segmentId)
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      isLoading.value = false
    }
  }

  const refetch = () => fetchSegment(id.value)

  watch(id, refetch, { immediate: true })

  return { data, isLoading, error, refetch }
}

type UseSaveSegmentReturn<T> = {
  save: (payload: T) => Promise<SegmentDetail | null>
  isLoading: Ref<boolean>
  error: Ref<Error | null>
}

export const useCreateSegment = (): UseSaveSegmentReturn<CreateCreatorSegmentDto> => {
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()
  const advertiserStore = useAdvertiserStore()

  const save = async (payload: CreateCreatorSegmentDto) => {
    const advertiserId = advertiserStore.advertiser?.id

    if (!advertiserId) {
      return null
    }

    try {
      isLoading.value = true
      error.value = null
      return await socialMediaClient.creatorSegments.advertiserCreate(advertiserId, payload)
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
      return null
    } finally {
      isLoading.value = false
    }
  }

  return { save, isLoading, error }
}

export const useUpdateSegment = (
  id: Ref<string>,
): UseSaveSegmentReturn<UpdateCreatorSegmentDto> => {
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const save = async (payload: UpdateCreatorSegmentDto) => {
    try {
      isLoading.value = true
      error.value = null
      return await socialMediaClient.creatorSegments.update(id.value, payload)
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
      return null
    } finally {
      isLoading.value = false
    }
  }

  return { save, isLoading, error }
}

export const useDeleteSegment = (): {
  remove: (id: string) => Promise<boolean>
  isLoading: Ref<boolean>
  error: Ref<Error | null>
} => {
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const remove = async (id: string) => {
    try {
      isLoading.value = true
      error.value = null
      await socialMediaClient.creatorSegments.remove(id)
      return true
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
      return false
    } finally {
      isLoading.value = false
    }
  }

  return { remove, isLoading, error }
}

type SegmentPreviewResponse = Parameters<typeof adaptSegmentPreview>[0]

/** The advertiser comes from the path, so the body never carries an advertiser group. */
const handleSegmentPreviewPayload = (filter: SegmentPreviewFilter): PreviewCreatorSegmentDto =>
  filter.segmentId
    ? {
        segment_id: filter.segmentId,
        manual_members: filter.manualMembers,
        excluded_members: filter.excludedMembers,
      }
    : {
        criteria: filter.criteria,
        manual_members: filter.manualMembers,
        excluded_members: filter.excludedMembers,
      }

/** Searches the current advertiser's segments by name. A newer query aborts the request still in flight. */
export const useSearchSegments = (query: Ref<string>) => {
  const data = ref<Segment[]>([])
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()
  const advertiserStore = useAdvertiserStore()
  let controller: AbortController | null = null

  const search = async () => {
    const advertiserId = advertiserStore.advertiser?.id

    if (!advertiserId) {
      return
    }

    controller?.abort()
    const { signal } = (controller = new AbortController())

    try {
      isLoading.value = true
      error.value = null

      const result = await socialMediaClient.creatorSegments.advertiserListSegments(
        advertiserId,
        { name: query.value.trim() || undefined, limit: SEGMENTS_PAGE_LIMIT },
        { signal },
      )

      if (!signal.aborted) {
        data.value = result.items
      }
    } catch (e: unknown) {
      if (signal.aborted) {
        return
      }

      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      if (!signal.aborted) {
        isLoading.value = false
      }
    }
  }

  watch([query, () => advertiserStore.advertiser?.id], search, { immediate: true })

  if (getCurrentScope()) {
    onScopeDispose(() => controller?.abort())
  }

  return { data, isLoading, error }
}

export const useGetSegmentPreview = (
  filterRef: Ref<SegmentPreviewFilter>,
): UseGetSegmentPreviewReturn => {
  const response = ref<{
    data: SegmentPreviewResponse
    criteria: SegmentPreviewFilter['criteria']
  } | null>(null)
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()
  const advertiserStore = useAdvertiserStore()
  let controller: AbortController | null = null

  const fetchPreview = async () => {
    const advertiserId = advertiserStore.advertiser?.id

    if (!advertiserId) {
      return
    }

    const filter = filterRef.value

    // A newer filter supersedes the preview in flight, so a slow older response
    // can never land on top of the one the user is now looking at.
    controller?.abort()
    const { signal } = (controller = new AbortController())

    try {
      isLoading.value = true
      error.value = null

      const data = await socialMediaClient.creatorSegments.advertiserPreview(
        advertiserId,
        handleSegmentPreviewPayload(filter),
        handleSegmentPreviewParams(filter),
        { signal },
      )

      if (signal.aborted) {
        return
      }

      // The matched values are keyed by the criteria that produced them, so the
      // two are kept together rather than read from a filter that may have moved on.
      response.value = { data: data as SegmentPreviewResponse, criteria: filter.criteria }
    } catch (e: unknown) {
      if (signal.aborted) {
        return
      }

      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      if (!signal.aborted) {
        isLoading.value = false
      }
    }
  }

  watch([filterRef, () => advertiserStore.advertiser?.id], fetchPreview, {
    immediate: true,
    deep: true,
  })

  if (getCurrentScope()) {
    onScopeDispose(() => controller?.abort())
  }

  const result = computed(() =>
    response.value ? adaptSegmentPreview(response.value.data, response.value.criteria) : null,
  )

  return {
    data: computed(() => result.value?.creators ?? []),
    totalCount: computed(() => result.value?.totalCount ?? 0),
    totalMembers: computed(() => result.value?.totalMembers ?? 0),
    totalPages: computed(() => result.value?.totalPages ?? 0),
    exceedsMemberCap: computed(() => result.value?.exceedsMemberCap ?? false),
    isLoading,
    error,
    refetch: fetchPreview,
  }
}
